import { useState } from 'react';
import { ArrowRight, MapPin, Filter, Ruler, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '918880331224';

const categories = ['All', 'Residential', 'Commercial', 'Interior', 'Renovation'];

const projects = [
  {
    id: 1,
    title: 'Modern Residential Complex',
    location: 'Kalaburagi',
    type: 'Residential',
    description: 'A stunning residential building with contemporary design and premium finishes.',
    area: '4500 sq.ft',
    image: 'https://customer-assets.emergentagent.com/job_akruti-3d-build/artifacts/xne8kquc_WhatsApp%20Image%202026-02-09%20at%2010.51.24%20AM.jpeg',
    featured: true,
  },
  {
    id: 2,
    title: 'Premium Villa Project',
    location: 'Gulbarga',
    type: 'Residential',
    description: 'Elegant villa construction with modern architecture and quality craftsmanship.',
    area: '3500 sq.ft',
    image: 'https://customer-assets.emergentagent.com/job_akruti-3d-build/artifacts/v83h0quh_WhatsApp%20Image%202026-02-09%20at%2010.51.26%20AM.jpeg',
    featured: true,
  },
  {
    id: 3,
    title: 'Luxury House Construction',
    location: 'Aland',
    type: 'Residential',
    description: 'Complete turnkey residential project with premium interiors and exteriors.',
    area: '5000 sq.ft',
    image: 'https://customer-assets.emergentagent.com/job_akruti-3d-build/artifacts/if4tnjag_WhatsApp%20Image%202026-02-09%20at%2010.51.25%20AM.jpeg',
    featured: true,
  },
  {
    id: 4,
    title: 'Luxury Living Room',
    location: 'Aland',
    type: 'Interior',
    description: 'Elegant interior design with premium furniture and lighting.',
    area: '800 sq.ft',
    image: 'https://images.unsplash.com/photo-1762544968153-b9b47435fefd?auto=format&fit=crop&w=800&q=60',
    featured: false,
  },
  {
    id: 5,
    title: 'Premium Bedroom Suite',
    location: 'Kalaburagi',
    type: 'Interior',
    description: 'Luxurious master bedroom with custom wardrobes and ambient lighting.',
    area: '450 sq.ft',
    image: 'https://images.unsplash.com/photo-1760067537116-de1f76fe8f95?auto=format&fit=crop&w=800&q=60',
    featured: false,
  },
  {
    id: 6,
    title: 'Modern Kitchen',
    location: 'Gulbarga',
    type: 'Interior',
    description: 'State-of-the-art modular kitchen with marble countertops.',
    area: '300 sq.ft',
    image: 'https://images.unsplash.com/photo-1742280879518-ada47b660ccd?auto=format&fit=crop&w=800&q=60',
    featured: false,
  },
  {
    id: 7,
    title: 'Home Renovation',
    location: 'Aland',
    type: 'Renovation',
    description: 'Complete home makeover transforming a dated space into modern luxury.',
    area: '2200 sq.ft',
    image: 'https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?auto=format&fit=crop&w=800&q=60',
    featured: false,
  },
  {
    id: 8,
    title: 'Commercial Space',
    location: 'Kalaburagi',
    type: 'Commercial',
    description: 'Modern commercial building with premium finishes and professional layout.',
    area: '8000 sq.ft',
    image: 'https://images.unsplash.com/photo-1747531301740-7c84b3ecaf23?auto=format&fit=crop&w=800&q=60',
    featured: false,
  },
];

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects.filter(p => p.type === activeCategory);

  const handleEnquiry = (projectTitle) => {
    const message = encodeURIComponent(`Hello! I am interested in a project similar to "${projectTitle}". Please share more details.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="pt-24 bg-[#ececec] min-h-screen" data-testid="projects-page">
      {/* Hero Section */}
      <section className="py-12 md:py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              Our Portfolio
            </span>
            <h1 className="font-heading text-4xl md:text-6xl text-white mt-4 font-bold">
              Completed<br />
              <span className="text-[#C9A646]">Projects</span>
            </h1>
            <p className="text-gray-400 text-base mt-4">
              Explore our portfolio of successfully completed construction and interior design projects
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 bg-[#f5f5f5] border-b border-gray-200 sticky top-20 md:top-24 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <Filter size={18} className="text-[#C9A646] flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-all rounded-lg ${
                  activeCategory === category
                    ? 'bg-[#C9A646] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects - Side by Side Layout */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-8">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow`}
                data-testid={`project-${project.id}`}
              >
                {/* Image */}
                <div className="md:w-1/2 h-64 md:h-auto">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="md:w-1/2 p-6 md:p-10 flex flex-col justify-center">
                  {/* Type Badge */}
                  <span className="inline-block bg-[#C9A646] text-white text-xs font-bold px-3 py-1 rounded-full w-fit uppercase tracking-wider">
                    {project.type}
                  </span>

                  {/* Title */}
                  <h2 className="font-heading text-2xl md:text-3xl text-gray-900 mt-4 font-bold">
                    {project.title}
                  </h2>

                  {/* Location & Area */}
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-gray-500 text-sm">
                    <span className="flex items-center gap-1">
                      <MapPin size={16} className="text-[#C9A646]" />
                      {project.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Ruler size={16} className="text-[#C9A646]" />
                      {project.area}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 mt-4 leading-relaxed">
                    {project.description}
                  </p>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleEnquiry(project.title)}
                    className="mt-6 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold py-3 px-6 rounded-xl inline-flex items-center gap-2 w-fit hover:opacity-90 transition-opacity"
                  >
                    <MessageCircle size={18} />
                    Get Similar Project
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#C9A646] to-[#E6C36A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-[#1a1a1a] font-bold">
            Ready to Start Your Project?
          </h2>
          <p className="text-[#1a1a1a]/80 mt-3">
            Let us help you create your dream space with our expert team
          </p>
          <button
            onClick={() => handleEnquiry('custom project')}
            className="mt-6 bg-[#1a1a1a] text-white font-semibold px-8 py-4 rounded-xl hover:bg-[#333] transition-colors inline-flex items-center gap-2"
          >
            <MessageCircle size={18} />
            Start Your Project
            <ArrowRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}
